<template>
  <div :id="id" class="chartContainer" />
</template>

<script>
import { isEmpty, upperFirst, each, isNil } from 'lodash-es'
import Vue from 'vue'
import rfConfigChartMixin from './rfConfigChartMixin'
import { multi as rfConstants } from '@/configuration/rfConfig'

export default {
  mixins: [rfConfigChartMixin],

  computed: {
    modemName () {
      if (isEmpty(this.modem)) return ''
      return this.modem.modem_name || 'Modem ' + this.id.split('-')[1]
    },

    chips () {
      if (isEmpty(this.modem)) return {}
      return this.modem.SX1301_conf
    },

    // Min frequency
    minFreq () {
      if (isEmpty(this.modem)) return 0
      return this.modem.board_rx_freq / 1000000 - this.modem.board_rx_bw / 1000000 / 2 - 1
    },

    // Max frequency
    maxFreq () {
      if (isEmpty(this.modem)) return 0
      return this.modem.board_rx_freq / 1000000 + this.modem.board_rx_bw / 1000000 / 2 + 1
    },

    // Graphic data
    dataProvider () {
      if (isEmpty(this.modem)) return []

      const rfConfig = []

      // modem bandwidth reception
      const modemFreqbandLow = Number(this.modem.board_rx_freq / 1000000) - (this.modem.board_rx_bw / 1000000 / 2)
      const modemFreqbandHigth = Number(this.modem.board_rx_freq / 1000000) + (this.modem.board_rx_bw / 1000000 / 2)
      rfConfig.push({
        t_text: this.modemName,
        freqband_low: modemFreqbandLow,
        freqband_high: modemFreqbandHigth,
        color: '#dee2e6',
        text_balloon: upperFirst(this.$t('gc-rfConfigChartMulti:modemBalloon', { freq: Number(this.modem.board_rx_freq / 1000000) })),
        width: 0.9
      })

      // Loop through chips
      each(this.chips, (chip, i) => {
        if (!chip) return true

        // Chip
        const chipFreqbandLow = Number(chip.chip_center_freq / 1000000) - (rfConstants.chipBandwidthMhz / 2)
        const chipFreqbandHigth = Number(chip.chip_center_freq / 1000000) + (rfConstants.chipBandwidthMhz / 2)
        rfConfig.push({
          t_text: 'Chip ' + chip.chip_rf_chain,
          freqband_low: chipFreqbandLow,
          freqband_high: chipFreqbandHigth,
          color: '#2196F3',
          text_balloon: upperFirst(this.$t('gc-rfConfigChartMulti:chipBalloon', { freq: Number(chip.chip_center_freq / 1000000) })),
          width: 0.8
        })

        // Multi SF channels
        for (let i = 0; i < 8; i++) {
          // Set enable value: true if attribute not present
          if (chip['chan_multiSF_' + i] && isNil(chip['chan_multiSF_' + i].enable)) Vue.set(chip['chan_multiSF_' + i], 'enable', true)

          if (chip['chan_multiSF_' + i].enable) {
            const multiFreq = chip['chan_multiSF_' + i].chan_rx_freq
            const multiFreqbandLow = Number(multiFreq / 1000000) - (rfConstants.multiSfBandwidth / 2)
            const multiFreqbandHigth = Number(multiFreq / 1000000) + (rfConstants.multiSfBandwidth / 2)
            rfConfig.push({
              t_text: 'Multi SF' + i,
              frequency: multiFreq,
              freqband_low: multiFreqbandLow,
              freqband_high: multiFreqbandHigth,
              color: '#4CAF50',
              text_balloon: upperFirst(this.$t('gc-rfConfigChartMulti:multiSfBalloon', { chip: chip.chip_rf_chain, freq: Number(multiFreq / 1000000) })),
              width: 0.4
            })
          }
        }

        // Mono SF channels
        // Set enable value: true if attribute not present
        if (chip.chan_LoRa_std && isNil(chip.chan_LoRa_std.enable)) Vue.set(chip.chan_LoRa_std, 'enable', true)
        if (chip.chan_LoRa_std && chip.chan_LoRa_std.enable) {
          const monoFreq = chip.chan_LoRa_std.chan_rx_freq
          const monoFreqbandLow = Number(monoFreq / 1000000) - (chip.chan_LoRa_std.bandwidth / 2 / 1000000)
          const monoFreqbandHigth = Number(monoFreq / 1000000) + (chip.chan_LoRa_std.bandwidth / 2 / 1000000)
          rfConfig.push({
            t_text: 'Lora MAC channel, ' + (chip.chan_LoRa_std.bandwidth / 1000) + 'kHz, SF' + chip.chan_LoRa_std.spread_factor,
            frequency: monoFreq,
            freqband_low: monoFreqbandLow,
            freqband_high: monoFreqbandHigth,
            color: '#FAB619',
            text_balloon: upperFirst(this.$t('gc-rfConfigChartMulti:monoSfBalloon', { chip: chip.chip_rf_chain, freq: Number(monoFreq / 1000000) })),
            width: 0.4
          })
        }

        // Lora FSK channel
        // Set enable value: true if attribute not present
        if (chip.chan_FSK && isNil(chip.chan_FSK.enable)) Vue.set(chip.chan_FSK, 'enable', true)
        if (chip.chan_FSK && chip.chan_FSK.enable) {
          const fskFreq = chip.chan_FSK.chan_rx_freq
          const fskFreqbandLow = Number(fskFreq / 1000000) - (chip.chan_FSK.bandwidth / 2 / 1000000)
          const fskFreqbandHigth = Number(fskFreq / 1000000) + (chip.chan_FSK.bandwidth / 2 / 1000000)
          rfConfig.push({
            t_text: 'FSK ' + (chip.chan_FSK.bit_rate / 1000) + 'kbps channel',
            frequency: fskFreq,
            freqband_low: fskFreqbandLow,
            freqband_high: fskFreqbandHigth,
            color: '#6f42c1',
            text_balloon: upperFirst(this.$t('gc-rfConfigChartMulti:fskBalloon', { chip: chip.chip_rf_chain, freq: Number(fskFreq / 1000000) })),
            width: 0.4
          })
        }
      })

      return [{
        empty_string: '',
        RF_config: rfConfig
      }]
    }
  }
}
</script>

<style scoped lang='scss'>
  .chartContainer {
    height: 200px;
  }
</style>
